import { Component, computed, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { StoreConfigService } from '@core/services/store-config.service';
import { CategoryService } from '@core/services/category.service';
import { Category } from '@core/models';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { SearchService, SearchSuggestion } from '@core/services/search.service';
import { SelectComponent } from '@shared/components/select/select.component';

@Component({
  selector: 'app-search-bar',
  imports: [SelectComponent],
  template: `
    <div class="relative w-full">
    <form (submit)="onSubmit($event)" class="flex items-center w-full rounded-full border-2 border-gray-200 focus-within:border-primary-600 bg-white p-1 transition-all shadow-sm">
      <div class="hidden sm:block shrink-0 border-r border-gray-200">
        <app-select variant="bare" ariaLabel="Categoría de la búsqueda" [options]="categoryOptions()" [(value)]="selectedCategory" />
      </div>
      <input
        maxlength="100"
        type="search"
        name="q"
        [value]="term()"
        (input)="onInput($any($event.target).value)"
        (keydown)="onKey($event)"
        (blur)="open.set(false)"
        (focus)="open.set(true)"
        autocomplete="off"
        role="combobox"
        aria-autocomplete="list"
        [attr.aria-expanded]="showSuggestions()"
        [placeholder]="placeholder()"
        class="w-full text-sm text-gray-800 bg-transparent px-4 py-1.5 focus:outline-none placeholder-gray-400"
      />
      <button
        type="submit"
        class="shrink-0 w-9 h-9 rounded-full bg-primary-600 text-white flex items-center justify-center hover:opacity-90 transition-opacity shadow-sm"
        aria-label="Buscar"
      >
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
      </button>
    </form>
    @if (showSuggestions()) {
      <!-- mousedown no quita el foco del input: así el clic llega antes de que (blur) cierre la lista -->
      <ul role="listbox" (mousedown)="$event.preventDefault()" class="absolute left-0 right-0 top-full mt-2 z-50 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
        @if (searchedAs()) {
          <li class="px-4 py-1.5 text-xs text-gray-500 bg-gray-50">Mostrando resultados para «{{ searchedAs() }}»</li>
        }
        @for (s of suggestions(); track s.slug; let i = $index) {
          <li role="option" [attr.aria-selected]="i === active()">
            <button type="button" (click)="goTo(s)" class="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50" [class.bg-gray-50]="i === active()">
              @if (s.image) {
                <img [src]="s.image" alt="" loading="lazy" class="w-10 h-10 rounded-md object-cover bg-gray-100 shrink-0" />
              } @else {
                <span class="w-10 h-10 rounded-md bg-gray-100 shrink-0"></span>
              }
              <span class="flex-1 min-w-0 text-sm text-gray-800 truncate">{{ s.name }}</span>
              <span class="text-sm font-semibold text-gray-900 shrink-0">\${{ s.price.toFixed(2) }}</span>
            </button>
          </li>
        }
        <li>
          <button type="button" (click)="search()" class="w-full px-4 py-2 text-left text-sm text-primary-600 font-medium hover:bg-gray-50 border-t border-gray-100">
            Ver todos los resultados de «{{ term().trim() }}»
          </button>
        </li>
      </ul>
    }
    </div>
  `,
})
export class SearchBarComponent {
  private router = inject(Router);
  private configService = inject(StoreConfigService);
  private categoryService = inject(CategoryService);
  private searchService = inject(SearchService);

  placeholder = input('Buscar productos o servicios...');
  initialTerm = input('');

  /** Vive en el header (layout reutilizado entre tiendas): se renueva al cambiar el slug. */
  categories = toSignal(
    toObservable(this.configService.currentSlug).pipe(switchMap(() => this.categoryService.getVisible())),
    { initialValue: [] as Category[] },
  );
  categoryOptions = computed(() => [{ value: '', label: 'Todas las categorías' }, ...this.categories().map((c) => ({ value: c.slug, label: c.name }))]);
  selectedCategory = signal('');
  term = signal('');
  open = signal(false);
  active = signal(-1);

  /** Sugerencias: espera 250 ms sin teclear, mínimo 2 letras, y descarta la respuesta vieja si ya se escribió otra cosa. */
  private result = toSignal(
    toObservable(this.term).pipe(
      map((t) => t.trim()),
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((q) => (q.length < 2 ? of(null) : this.searchService.suggest(q).pipe(catchError(() => of(null))))),
    ),
    { initialValue: null },
  );
  suggestions = computed<SearchSuggestion[]>(() => this.result()?.data ?? []);
  searchedAs = computed(() => this.result()?.searchedAs);
  showSuggestions = computed(() => this.open() && this.term().trim().length >= 2 && this.suggestions().length > 0);

  constructor() {
    this.term.set(this.initialTerm());
  }

  onInput(value: string): void {
    this.term.set(value);
    this.open.set(true);
    this.active.set(-1);
  }

  onKey(e: KeyboardEvent): void {
    const n = this.suggestions().length;
    if (e.key === 'Escape') this.open.set(false);
    else if (this.showSuggestions() && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      this.active.update((i) => (e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n));
    } else if (e.key === 'Enter' && this.showSuggestions() && this.active() >= 0) {
      e.preventDefault();
      this.goTo(this.suggestions()[this.active()]);
    }
  }

  goTo(s: SearchSuggestion): void {
    this.open.set(false);
    this.router.navigate(['/' + this.configService.currentSlug(), 'productos', s.slug]);
  }

  onSubmit(e: Event): void {
    e.preventDefault();
    this.search();
  }

  search(): void {
    this.open.set(false);
    const q = this.term().trim();
    const cat = this.selectedCategory();
    const queryParams: Record<string, string> = {};
    if (q) queryParams['q'] = q;
    if (cat) queryParams['categoria'] = cat;

    if (q || cat) {
      this.router.navigate(['/' + this.configService.currentSlug() + '/buscar'], { queryParams });
    }
  }
}
